<script>
import IconArrowheadLeft from '@/components/icons/IconArrowheadLeft'
import IconArrowheadRight from '@/components/icons/IconArrowheadRight'

function getWeekNumber(inputDate, weeks) {
    const targetDateStr = inputDate.toDateString()

    const matchedWeek = weeks.find(week => {
        return week.days.some(({date}) => date.toDateString() === targetDateStr)
    })

    return matchedWeek ? matchedWeek.weekNumber : null
}

function isTodayHoliday(d) {
    const year = d.getFullYear()

    const holidays = [
        new Date(year, 0, 1), // Jour de l'An (1er janvier)
        new Date(year, 4, 1), // Fête du Travail (1er mai)
        new Date(year, 4, 8), // Victoire 1945 (8 mai)
        new Date(year, 6, 14), // Fête Nationale (14 juillet)
        new Date(year, 7, 15), // Assomption (15 août)
        new Date(year, 10, 1), // Toussaint (1er novembre)
        new Date(year, 10, 11), // Armistice 1918 (11 novembre)
        new Date(year, 11, 25), // Noël (25 décembre)
    ]

    return holidays.some(
        (holiday) =>
            holiday.getDate() === d.getDate() &&
            holiday.getMonth() === d.getMonth() &&
            holiday.getFullYear() === d.getFullYear()
    )
}

function buildSlots(currentDay) {
    const slots = []
    const startHour = 9
    const endHour = 19
    const duration = 2
    // const now = new Date('2026-07-03T17:58:48+02:00')
    const now = new Date()

    const formatTime = (dateObj) => {
        const hour = dateObj.toLocaleTimeString('fr-FR', {
            timeZone: 'Europe/Paris',
            hour: '2-digit',
            hour12: false,
        }).padStart(2, '0').replace(' ', '')

        const minute = dateObj.toLocaleTimeString('fr-FR', {
            timeZone: 'Europe/Paris',
            minute: '2-digit',
            hour12: false,
        }).padStart(2, '0').replace(' ', '')

        return minute === '00' ? `${hour}` : `${hour}${minute}`
    }

    for (let hour = startHour; hour + duration <= endHour; hour += duration) {
        const slotStart = new Date(currentDay)
        slotStart.setHours(hour, 0, 0, 0)

        const slotEnd = new Date(currentDay)
        slotEnd.setHours(hour + duration, 0, 0, 0)

        slots.push({
            start: slotStart,
            end: slotEnd,
            selectable: slotStart > now,
            label: `Entre ${formatTime(slotStart)} et ${formatTime(slotEnd)}`,
        })
    }

    return slots
}
export default {
    name: 'BaseTimeSlotPicker',

    props: {
        value: {
            type: Object,
        },

        options: {
            type: Object,
            default(){
                return {
                    saturdayWorked: false,
                    weeksNumber: 3
                }
            }
        },
    },

    components: {
        IconArrowheadRight,
        IconArrowheadLeft,
    },

    data() {
        const weeks = []
        // const today = new Date('2026-07-03T17:58:48+02:00')
        const today = new Date()

        const dayOfWeek = today.getDay()
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1

        const monday = new Date(today)
        monday.setDate(today.getDate() - diffToMonday)

        const nonWorkedDays = this.options.saturdayWorked ? [6] : [6, 5]

        for (let w = 0; w < this.options.weeksNumber; w++) {
            const weekDays = []

            for (let d = 0; d < 7; d++) {
                const day = new Date(monday)
                day.setDate(monday.getDate() + w * 7 + d)

                const isWorked = !nonWorkedDays.includes(d) && !isTodayHoliday(day)
                const isPast = day < today

                const slots = buildSlots(day)
                const hasSelectableSlots = slots.filter(({selectable}) => selectable).length

                weekDays.push({
                    date: day,
                    selectable: isWorked && !isPast && hasSelectableSlots,
                    slots
                })
            }

            weeks.push({
                weekNumber: w,
                days: weekDays,
            })
        }

        let initialWeek = null
        let initialDay = null

        outerLoop:
        for (const week of weeks) {
            for (const day of week.days) {
                if (day.selectable) {
                    const firstSelectableSlot = day.slots.find(slot => slot.selectable)

                    if (firstSelectableSlot) {
                        initialWeek = week.weekNumber
                        initialDay = day
                        break outerLoop
                    }
                }
            }
        }

        return {
            weeks,
            currentWeekNumber: initialWeek,
            currentDay: initialDay,
        }
    },

    computed: {
        currentSlot() {
            // Alias for prop value
            return this.value
        },

        currentWeek() {
            return this.weeks.find(({ weekNumber }) => {
                return weekNumber === this.currentWeekNumber
            })
        },

        slots() {
            return this.currentDay.slots
        },
    },

    watch: {
        currentDay() {
            const selectableSlot = this.slots.find(({ selectable }) => selectable)
            if (selectableSlot) {
                this.$emit('valueChange', selectableSlot)
            }
        },

        value() {
            if (!this.value) {
                return
            }
            this.currentWeekNumber = getWeekNumber(this.value.start, this.weeks)
        },
    },

    mounted() {
        if (!this.value) {
            this.$emit('valueChange', this.slots.find(({ selectable }) => selectable))
        }
    },

    methods: {
        handlePrevWeekClick() {
            this.currentWeekNumber = this.currentWeekNumber - 1
            const selectableDay = this.currentWeek.days.find(({ selectable }) => selectable)
            if (selectableDay) {
                this.currentDay = selectableDay
            } else {
                this.currentDay = this.currentWeek.days[0]
            }
        },

        handleNextWeekClick() {
            this.currentWeekNumber = this.currentWeekNumber + 1
            const selectableDay = this.currentWeek.days.find(({ selectable }) => selectable)
            if (selectableDay) {
                this.currentDay = selectableDay
            } else {
                this.currentDay = this.currentWeek.days[0]
            }
        },

        handleDayClick(day) {
            this.currentDay = day
        },

        handleSlotClick(slot) {
            this.$emit('valueChange', slot)
        },
    },
}
</script>

<template>
    <div class="base-time-slot-picker">
        <div class="month-title">
            {{ currentWeek.days[0].date.toLocaleString('fr-FR', { timeZone: 'Europe/Paris', month: 'long', year: 'numeric' }) }}
        </div>
        <div class="week-title">
            <div>
                Du {{ currentWeek.days[0].date.toLocaleString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'numeric', year: 'numeric' }) }} au
                 {{ currentWeek.days[6].date.toLocaleString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'numeric', year: 'numeric' }) }}
            </div>
            <div class="week-controls">
                <div
                    @click="handlePrevWeekClick"
                    @keydown.enter="handlePrevWeekClick"
                    @keydown.space="handlePrevWeekClick"
                    :tabindex="this.currentWeekNumber === 0 ? -1 : 0"
                    class="week-arrow"
                    :class="{ disabled: this.currentWeekNumber === 0 }">
                    <IconArrowheadLeft/>
                </div>
                <div
                    @click="handleNextWeekClick"
                    @keydown.enter="handleNextWeekClick"
                    @keydown.space="handleNextWeekClick"
                    :tabindex="this.currentWeekNumber === weeks.length - 1 ? -1 : 0"
                    class="week-arrow"
                    :class="{ disabled: this.currentWeekNumber === weeks.length - 1 }">
                    <IconArrowheadRight/>
                </div>
            </div>
        </div>
        <ul class="days-list">
            <li v-for="(day, index) in currentWeek.days"
            :key="index"
            class="day-item"
            :class="{ selectable: day.selectable, selected: day.date === currentDay.date }"
            @click="handleDayClick(day)"
            @keydown.enter="handleDayClick(day)"
            @keydown.space="handleDayClick(day)"
            :tabindex="day.selectable ? 0 : -1">
                <span class="day-name">{{ day.date.toLocaleString('fr-FR', { weekday: 'short' }).slice(0, 2) }}</span>
                <span class="day-number">{{ day.date.toLocaleString('fr-FR', { day: 'numeric' }) }}</span>
            </li>
        </ul>

        <ul class="slots-list">
            <li
                v-for="(slot, i) in slots"
                :key="i"
                @click="handleSlotClick(slot)"
                @keydown.enter="handleSlotClick(slot)"
                @keydown.space="handleSlotClick(slot)"
                :tabindex="slot.selectable ? 0 : -1"
                class="slot-item"
                :class="{ selectable: slot.selectable, selected: currentSlot ? currentSlot.start === slot.start : false }"
            >
                {{ slot.label }}
            </li>
        </ul>
    </div>
</template>
<style lang="stylus">
.base-time-slot-picker
    max-width 335px
    background #FFF
    padding 10px
    border-radius 10px

    .month-title
        text-transform capitalize
        font-size 16px
        font-weight 500

    .week-title
        font-size 13px
        font-weight 500
        color #000
        margin-top 8px
        display flex
        justify-content space-between
        align-items center

    .days-list
        display flex
        padding 0
        list-style none
        border-bottom 1px solid var(--AkGreyscaleMediumLtGrey)
        margin 0
        padding-bottom 20px
        padding-top 20px
        margin-bottom 20px

    .day-item
        display inline-flex
        flex-direction column
        align-items center
        justify-content center
        padding 0 9px
        cursor pointer
        color var(--AkGreyscaleMediumLtGrey)
        pointer-events none
        font-size 16px
        font-weight 500
        border-radius 5px

        .day-name
            text-transform capitalize

        .day-number
            width 30px
            height 30px
            border-radius 50%
            display flex
            align-items center
            justify-content center
            background transparent
            margin-top 5px

        &.selectable.selected
            color var(--color1Inverse)

            .day-number
                background var(--color1)

        &.selectable
            pointer-events all
            color var(--color9)

            .day-number
                background var(--colorMinor)

            &:focus-visible
                outline 2px solid black

    .slots-list
        margin 0
        padding 0
        list-style none

        .slot-item
            text-align center
            font-size 16px
            font-weight 500
            cursor pointer
            color var(--AkGreyscaleMediumLtGrey)
            pointer-events none
            padding-top 14px
            padding-bottom 14px

            &.selectable.selected
            &.selectable:hover
                color var(--color1Inverse)
                background var(--color1)

            &.selectable
                pointer-events all
                color var(--color9)

                &:focus-visible
                    outline 2px solid black

    .week-arrow
        cursor pointer
        display inline-flex
        align-items center
        justify-content center
        width 25px
        height 25px
        border-radius 50%
        color #FFF
        background  var(--AkGreyscaleBlack)
        margin-left 5px

        svg
            width 15px


        &.disabled
            background  var(--AkGreyscaleMediumLtGrey)
            pointer-events none

        &:not(.disabled):focus-visible
            outline 2px solid black
            background var(--color1)
</style>
